import { html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { styleMap } from 'lit/directives/style-map.js';
import { clamp, isEmpty, isNil } from 'lodash-es';

import i18n from 'portal-base/src/decorators/i18n';

import { KiDsPwaComponent } from '../../base/ki-ds-pwa-component';
import nls from '../../locales/indexes.nls';
import style from './ki-ds-pwa-alarm-bar.css';

const CLASS_NAME = 'ki-ds-pwa-alarm-bar';

const FALLBACK_COLOR = 'var(--color-classes-fill-nodata)';
const MS_PER_MINUTE = 60000;

// Marker position along the alarm bar.
const BAR_START_PERCENT = 0;
const BAR_END_PERCENT = 100;

@i18n(nls)
@customElement(CLASS_NAME)
export class KiDsPwaAlarmBar extends KiDsPwaComponent {
    static styles = style;

    static properties = {
        alarmConfig: { type: Object },
        eventDefinitionType: { type: String },
        latestValue: { type: Number },
        alarmValue: { type: Number },
        classificationTemplate: { type: Object },
        showOuterLimits: { type: Boolean },
    };

    constructor() {
        super();
        this.showOuterLimits = false;
    }

    get valueClasses() {
        return this.alarmConfig?.eventDefinitionConfig?.valueClasses ?? [];
    }

    get visibleClasses() {
        return this.valueClasses.filter((vc) => this.showOuterLimits || !this.__isOuterLimit(vc));
    }

    get isOutdated() {
        return this.eventDefinitionType?.startsWith('OUTDATED');
    }

    get normalizedValue() {
        if (this.isOutdated && !isNil(this.alarmValue)) {
            return this.alarmValue * MS_PER_MINUTE;
        }
        return this.latestValue;
    }

    __findTemplateEntry(valueClass) {
        const templateEntries = this.classificationTemplate?.templateEntries;
        if (!templateEntries) return null;
        return (
            templateEntries.find((e) => e.id === valueClass.description) ??
            templateEntries.find((e) => e.name === valueClass.name) ??
            null
        );
    }

    __isOuterLimit(valueClass) {
        const entry = this.__findTemplateEntry(valueClass);
        return entry?.settings === 'outerlimit';
    }

    __getColorForClass(valueClass) {
        const entry = this.__findTemplateEntry(valueClass);
        return entry?.iconBackGroundColor ?? FALLBACK_COLOR;
    }

    __formatValue(value) {
        if (isNil(value)) return '∞';
        if (this.isOutdated) {
            return `${Math.round(value / MS_PER_MINUTE)} ${this.i18n.t('minutesShortName')}`;
        }
        return value;
    }

    __calculateMarkerPosition(classes) {
        const value = this.normalizedValue;
        if (isEmpty(classes) || isNil(value)) {
            return null;
        }

        const segmentWidth = 1 / classes.length;

        for (let i = 0; i < classes.length; i++) {
            const vc = classes[i];
            const min = vc.minValue;
            const max = vc.maxValue;

            if (min === null && value <= max) {
                return BAR_START_PERCENT;
            }
            if (max === null && value >= min) {
                return BAR_END_PERCENT;
            }
            if (min !== null && max !== null && value >= min && value <= max) {
                const ratio = (value - min) / (max - min);
                return (i * segmentWidth + ratio * segmentWidth) * BAR_END_PERCENT;
            }
        }

        // if value is outside the visible classes clamp to the nearest edge.
        const firstClass = classes[0];
        const lastClass = classes[classes.length - 1];
        const lowerBound = firstClass.minValue ?? firstClass.maxValue;
        const upperBound = lastClass.maxValue ?? lastClass.minValue;

        if (!isNil(lowerBound) && value < lowerBound) return BAR_START_PERCENT;
        if (!isNil(upperBound) && value > upperBound) return BAR_END_PERCENT;

        return null;
    }

    willUpdate() {
        this.__visibleClasses = this.visibleClasses;
        this.__markerPosition = this.__calculateMarkerPosition(this.__visibleClasses);
    }

    render() {
        const classes = this.__visibleClasses;
        if (isEmpty(classes) || !this.eventDefinitionType) return nothing;

        const markerPosition = this.__markerPosition;

        return html`
            <div class="alarm-bar-container">
                <ul class="alarm-bar">
                    ${classes.map(
            (vc, i) => html`
                            <li
                                style=${styleMap({
                borderBottom: `var(--metrics-spacing-xs) solid ${this.__getColorForClass(vc)}`,
            })}
                            >
                                <span class="label-left">${this.__formatValue(vc.minValue)}</span>
                                ${i === classes.length - 1
                    ? html`<span class="label-right"
                                          >${this.__formatValue(vc.maxValue)}</span
                                      >`
                    : nothing}
                            </li>
                        `,
        )}
                </ul>
                ${markerPosition !== null
                ? html`<div
                          class=${classMap({
                    'alarm-pointer': true,
                    'rotated-left': markerPosition < BAR_START_PERCENT,
                    'rotated-right': markerPosition > BAR_END_PERCENT,
                })}
                          style=${styleMap({ left: `${clamp(markerPosition, BAR_START_PERCENT, BAR_END_PERCENT)}%` })}
                      ></div>`
                : nothing}
            </div>
        `;
    }
}
